// Jun-2026 — PO Payments: read-only payment trail (PI → Bill Payment →
// Payment Voucher) against this project's Purchase Orders. Gated by the
// dedicated `po_payments.view` permission; ₹ masked without pr_commercials.
import React, { useEffect, useState } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { api, formatINR, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ShieldAlert, RefreshCw, Landmark, Send, Search } from "lucide-react";
import { RequestPaymentModal, PaymentRequestsPanel } from "@/components/PoPaymentRequests";

const PAY_BADGE = {
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Partially Paid": "bg-amber-100 text-amber-800 border-amber-300",
  Posted: "bg-sky-100 text-sky-800 border-sky-300",
  Unpaid: "bg-rose-50 text-rose-700 border-rose-200",
  "Not Invoiced": "bg-slate-100 text-slate-500 border-slate-300",
};
const VCH_BADGE = {
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Posted: "bg-sky-100 text-sky-800 border-sky-300",
  Approved: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
};
const badge = (map, s) => (
  <span className={`inline-block border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${map[s] || "bg-slate-100 text-slate-600 border-slate-300"}`}>{s}</span>
);
// Aug-2026 (user report) — POs stay requestable AFTER invoicing starts:
// PI creation flips PO status to Partially Invoiced / Converted, which used
// to hide "Request Payment" even with requestable balance left.
const REQUESTABLE_PO_STATUSES = ["Approved", "Partially Invoiced", "Converted"];
const inr = (v) => (v == null ? "—" : formatINR(v));

function PaymentRows({ inv }) {
  return (
    <div className="ml-2 md:ml-6 mb-2 border-l-2 border-slate-200 pl-3 space-y-1" data-testid={`po-pay-invoice-${inv.invoice_number?.replace(/\//g, "-")}`}>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="font-mono font-bold">{inv.invoice_number}</span>
        <span className="text-slate-500">{formatDate(inv.invoice_date)}</span>
        <span className="text-slate-600">Net payable: <b className="tabular-nums">{inr(inv.net_payable)}</b></span>
        <span className="text-slate-600">Paid: <b className="tabular-nums text-emerald-700">{inr(inv.paid)}</b></span>
        {inv.shared_across_pos && <span className="text-[9px] text-slate-400" title="This invoice covers multiple POs — amounts shown are this PO's share">shared invoice · PO share</span>}
      </div>
      {inv.awaiting_processing && (
        <div className="text-[10px] text-amber-700" data-testid="po-pay-awaiting">Awaiting payment processing in Accounts (bill outstanding).</div>
      )}
      {(inv.payments || []).map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1"
          data-testid={`po-pay-voucher-${p.voucher_number?.replace(/\//g, "-")}`}>
          {badge(VCH_BADGE, p.status)}
          <span className="font-mono font-bold">{p.voucher_number}</span>
          <span className="text-slate-500">{formatDate(p.paid_at || p.voucher_date)}</span>
          <span className="inline-flex items-center gap-1 text-slate-600"><Landmark size={11} />{p.bank_ledger_name || "—"}</span>
          <span className="text-slate-500">Ref/UTR: <b>{p.payment_reference_no || "—"}</b></span>
          <span className="ml-auto tabular-nums font-bold">{inr(p.amount)}</span>
        </div>
      ))}
      {!inv.awaiting_processing && (inv.payments || []).length === 0 && (
        <div className="text-[10px] text-slate-400">No payment vouchers yet.</div>
      )}
    </div>
  );
}

// Payment trail block for "other PrimeWork payments" rows (desktop + mobile).
function OtherTrail({ r }) {
  return (
    <div className="ml-1 mb-2 border-l-2 border-slate-200 pl-3 space-y-1">
      {r.awaiting_processing && (
        <div className="text-[10px] text-amber-700">Awaiting payment processing in Accounts (bill outstanding).</div>
      )}
      {(r.payments || []).map((p, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1"
          data-testid={`po-other-voucher-${p.voucher_number?.replace(/\//g, "-")}`}>
          {badge(VCH_BADGE, p.status)}
          <span className="font-mono font-bold">{p.voucher_number}</span>
          <span className="text-slate-500">{formatDate(p.paid_at || p.voucher_date)}</span>
          <span className="inline-flex items-center gap-1 text-slate-600"><Landmark size={11} />{p.bank_ledger_name || "—"}</span>
          <span className="text-slate-500">Ref/UTR: <b>{p.payment_reference_no || "—"}</b></span>
          {p.batch_number && <span className="text-slate-500">Batch: <b className="font-mono">{p.batch_number}</b></span>}
          <span className="ml-auto tabular-nums font-bold">{inr(p.amount)}</span>
        </div>
      ))}
      {!r.awaiting_processing && (r.payments || []).length === 0 && (
        <div className="text-[10px] text-slate-400">No payment vouchers yet.</div>
      )}
    </div>
  );
}

export default function PoPaymentsSection({ project, refreshKey = 0 }) {
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({});
  const [requestFor, setRequestFor] = useState(null);
  const [reqRefresh, setReqRefresh] = useState(0);
  const canRequest = hasPerm("po_payment_request", "create");
  const canSeeRequests = hasPerm("po_payment_request", "view");
  const [q, setQ] = useState("");
  const filteredPos = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return data?.pos || [];
    const hit = (v) => String(v || "").toLowerCase().includes(t);
    return (data?.pos || []).filter((po) =>
      hit(po.po_number) || hit(po.vendor_name) || hit(po.vendor_code)
      || hit(po.payment_status) || hit(po.po_status)
      || (po.invoices || []).some((inv) =>
        hit(inv.invoice_number) || hit(inv.pi_status)
        || (inv.batch_numbers || []).some(hit)
        || (inv.payments || []).some((p) =>
          hit(p.voucher_number) || hit(p.payment_reference_no)
          || hit(p.bank_ledger_name) || hit(p.status) || hit(p.batch_number))));
  }, [data, q]);
  const filteredOthers = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    const rows = data?.other_payments || [];
    if (!t) return rows;
    const hit = (v) => String(v || "").toLowerCase().includes(t);
    return rows.filter((r) =>
      hit(r.invoice_number) || hit(r.vendor_name) || hit(r.pi_status)
      || hit(r.payment_status) || (r.po_refs || []).some(hit)
      || (r.batch_numbers || []).some(hit)
      || (r.payments || []).some((p) =>
        hit(p.voucher_number) || hit(p.payment_reference_no)
        || hit(p.bank_ledger_name) || hit(p.status) || hit(p.batch_number)));
  }, [data, q]);
  const [openOther, setOpenOther] = useState({});
  const poPager = usePager(filteredPos, 10);
  const otherPager = usePager(filteredOthers, 10);
  const load = () =>
    api.get(`/projects/${project.id}/po-payments`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "PO Payments are not available"));
  useEffect(() => { load(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Aug-2026 — parent header refresh (section-aware) bumps refreshKey.
  useEffect(() => {
    if (!refreshKey) return;
    load();
    setReqRefresh((k) => k + 1);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = async () => {
    setRefreshing(true);
    setReqRefresh((k) => k + 1);          // also refreshes the Payment Requests panel
    await load();
    setRefreshing(false);
    toast.success("Payment data refreshed");
  };

  if (err) return (
    <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm" data-testid="po-payments-forbidden">
      <ShieldAlert size={16} /> {err}
    </div>
  );
  if (!data) return <div className="text-xs text-slate-400 py-8 text-center" data-testid="po-payments-loading">Loading payment trail…</div>;

  const t = data.totals || {};
  const cell = (label, v, testid, cls = "text-slate-900") => (
    <div className="flex-1 min-w-[130px] border border-slate-200 rounded-lg bg-white px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className={`text-sm font-bold tabular-nums ${cls}`}>{v}</div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="po-payments-section">
      <div className="flex flex-wrap gap-2">
        {cell("Purchase Orders", t.po_count ?? 0, "po-pay-total-count")}
        {cell("PO Value (inc. GST)", inr(t.po_total_inc_gst), "po-pay-total-value")}
        {cell("Invoiced (Net Payable)", inr(t.invoiced_net_payable), "po-pay-total-invoiced")}
        {cell("Paid", inr(t.paid), "po-pay-total-paid", "text-emerald-700")}
        {cell("Balance Payable", inr(t.balance), "po-pay-total-balance", "text-rose-700")}
        {(data.other_payments || []).length > 0 &&
          cell("Other PrimeWork Paid", inr(data.other_totals?.paid), "po-pay-other-paid", "text-emerald-700")}
        {cell("Net Total Paid",
          t.paid == null ? "—" : inr((Number(t.paid) || 0) + (Number(data.other_totals?.paid) || 0)),
          "po-pay-net-total-paid", "text-emerald-800")}
        {(Number(data.opening_expenditure) || 0) > 0 && (
          <>
            {cell("Opening Expenditure (Prev. ERP)", inr(data.opening_expenditure),
              "po-pay-opening-exp", "text-indigo-700")}
            {cell("Grand Total (incl. Opening)",
              t.paid == null ? "—" : inr((Number(t.paid) || 0) + (Number(data.other_totals?.paid) || 0) + (Number(data.opening_expenditure) || 0)),
              "po-pay-grand-total", "text-indigo-900")}
          </>
        )}
      </div>
      {!data.commercials_visible && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-1.5" data-testid="po-pay-masked-note">
          ₹ values are hidden — you do not hold the PR Commercials permission. Statuses, vouchers, banks and reference numbers remain visible.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100">
          <div className="relative w-full sm:w-80">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="po-payments-search" className="border border-slate-300 rounded pl-6 pr-2 py-1.5 text-xs w-full"
              placeholder="Search PO / vendor / PI / voucher / UTR / bank / batch / status…"
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {q && <span className="text-[10px] text-slate-500" data-testid="po-payments-search-count">{filteredPos.length} match{filteredPos.length === 1 ? "" : "es"}</span>}
        </div>
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-xs" data-testid="po-payments-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
              <th className="px-2 py-1.5 w-6"></th>
              <th className="px-2 py-1.5">PO Number</th>
              <th className="px-2 py-1.5">Date</th>
              <th className="px-2 py-1.5">Vendor</th>
              <th className="px-2 py-1.5 text-right">PO Value (inc. GST)</th>
              <th className="px-2 py-1.5 text-right">Invoiced</th>
              <th className="px-2 py-1.5 text-right">Paid</th>
              <th className="px-2 py-1.5 text-right">Balance</th>
              <th className="px-2 py-1.5 text-right" title="PO value minus payment requests in flight (PI Requested / PI Created)">Requestable</th>
              <th className="px-2 py-1.5">Payment Status</th>
              {canRequest && <th className="px-2 py-1.5"></th>}
            </tr>
          </thead>
          <tbody>
            {poPager.slice.map((po) => (
              <React.Fragment key={po.po_id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`po-pay-row-${po.po_number?.replace(/\//g, "-")}`}
                  onClick={() => setOpen((o) => ({ ...o, [po.po_id]: !o[po.po_id] }))}>
                  <td className="px-2 py-1.5 text-slate-400">{open[po.po_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] font-bold">{po.po_number}</td>
                  <td className="px-2 py-1.5">{formatDate(po.po_date)}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={po.vendor_name}>{po.vendor_code ? `${po.vendor_code} · ` : ""}{po.vendor_name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{inr(po.po_total_inc_gst)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{inr(po.invoiced_net_payable)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-bold">{inr(po.paid)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{inr(po.balance)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${po.requestable_balance === 0 ? "text-slate-400" : "text-sky-700"}`}
                    data-testid={`po-requestable-${po.po_number?.replace(/\//g, "-")}`}>
                    {inr(po.requestable_balance)}
                  </td>
                  <td className="px-2 py-1.5">{badge(PAY_BADGE, po.payment_status)}</td>
                  {canRequest && (
                    <td className="px-2 py-1.5">
                      {REQUESTABLE_PO_STATUSES.includes(po.po_status) && po.requestable_balance !== 0 && (
                        <button data-testid={`po-request-payment-${po.po_number?.replace(/\//g, "-")}`}
                          onClick={(e) => { e.stopPropagation(); setRequestFor(po); }}
                          className="text-[#F97316] hover:underline font-bold inline-flex items-center gap-0.5 whitespace-nowrap">
                          <Send size={11} /> Request Payment
                        </button>
                      )}
                      {REQUESTABLE_PO_STATUSES.includes(po.po_status) && po.requestable_balance === 0 && (
                        <span className="text-[9px] uppercase font-bold text-slate-400 whitespace-nowrap" data-testid={`po-fully-requested-${po.po_number?.replace(/\//g, "-")}`}>Fully Requested</span>
                      )}
                    </td>
                  )}
                </tr>
                {open[po.po_id] && (
                  <tr className="border-t border-slate-50">
                    <td></td>
                    <td colSpan={canRequest ? 10 : 9} className="py-2">
                      {(po.invoices || []).length === 0
                        ? <div className="text-[11px] text-slate-400 ml-1" data-testid="po-pay-no-invoices">No purchase invoices against this PO yet.</div>
                        : po.invoices.map((inv) => <PaymentRows key={inv.pi_id} inv={inv} />)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filteredPos.length === 0 && (
              <tr><td colSpan={canRequest ? 10 : 9} className="px-2 py-6 text-center text-slate-400" data-testid="po-payments-empty">No Purchase Orders match.</td></tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Mobile: stacked PO payment cards */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="po-payments-cards">
          {poPager.slice.map((po) => (
            <div key={po.po_id} className="p-3 space-y-1.5" data-testid={`po-pay-card-${po.po_number?.replace(/\//g, "-")}`}>
              <button className="w-full text-left" onClick={() => setOpen((o) => ({ ...o, [po.po_id]: !o[po.po_id] }))}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-slate-400 shrink-0">{open[po.po_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
                  <span className="font-mono text-[11px] font-bold">{po.po_number}</span>
                  {badge(PAY_BADGE, po.payment_status)}
                  <span className="ml-auto text-[10px] text-slate-400">{formatDate(po.po_date)}</span>
                </div>
                <div className="text-xs text-slate-600 break-words mt-0.5">{po.vendor_code ? `${po.vendor_code} · ` : ""}{po.vendor_name}</div>
              </button>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                <span className="text-slate-500">PO Value: <b className="tabular-nums text-slate-800">{inr(po.po_total_inc_gst)}</b></span>
                <span className="text-slate-500">Invoiced: <b className="tabular-nums text-slate-800">{inr(po.invoiced_net_payable)}</b></span>
                <span className="text-emerald-700">Paid: <b className="tabular-nums">{inr(po.paid)}</b></span>
                <span className="text-rose-700">Balance: <b className="tabular-nums">{inr(po.balance)}</b></span>
                <span className={po.requestable_balance === 0 ? "text-slate-400" : "text-sky-700"}>Requestable: <b className="tabular-nums">{inr(po.requestable_balance)}</b></span>
              </div>
              {canRequest && REQUESTABLE_PO_STATUSES.includes(po.po_status) && (
                po.requestable_balance !== 0 ? (
                  <button data-testid={`po-request-payment-m-${po.po_number?.replace(/\//g, "-")}`}
                    onClick={() => setRequestFor(po)}
                    className="w-full inline-flex items-center justify-center gap-1 border border-[#F97316] text-[#F97316] text-xs font-bold uppercase tracking-wider py-1.5 rounded hover:bg-orange-50">
                    <Send size={11} /> Request Payment
                  </button>
                ) : (
                  <div className="text-[9px] uppercase font-bold text-slate-400">Fully Requested</div>
                )
              )}
              {open[po.po_id] && (
                (po.invoices || []).length === 0
                  ? <div className="text-[11px] text-slate-400" data-testid="po-pay-no-invoices-m">No purchase invoices against this PO yet.</div>
                  : po.invoices.map((inv) => <PaymentRows key={inv.pi_id} inv={inv} />)
              )}
            </div>
          ))}
          {filteredPos.length === 0 && (
            <div className="p-4 text-center text-slate-400 text-xs">No Purchase Orders match.</div>
          )}
        </div>
        <Paginator pager={poPager} testid="po-payments-pager" />
      </div>
      {(data.other_payments || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="po-other-payments-section">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100">
            <div className="text-[11px] font-bold text-slate-700">Payments recorded in PrimeWork against this project</div>
            <div className="text-[10px] text-slate-400">standard / direct purchase invoices tagged to this project (outside Project-Ops POs)</div>
            <div className="ml-auto flex flex-wrap gap-3 text-[10px]">
              <span>Invoiced: <b className="tabular-nums">{inr(data.other_totals?.invoiced_net_payable)}</b></span>
              <span className="text-emerald-700">Paid: <b className="tabular-nums">{inr(data.other_totals?.paid)}</b></span>
              <span className="text-rose-700">Balance: <b className="tabular-nums">{inr(data.other_totals?.balance)}</b></span>
            </div>
          </div>
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[760px] text-xs" data-testid="po-other-payments-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
                <th className="px-2 py-1.5 w-6"></th>
                <th className="px-2 py-1.5">Invoice</th>
                <th className="px-2 py-1.5">Date</th>
                <th className="px-2 py-1.5">Vendor</th>
                <th className="px-2 py-1.5">PO Ref</th>
                <th className="px-2 py-1.5">Batch No</th>
                <th className="px-2 py-1.5 text-right">Net Payable</th>
                <th className="px-2 py-1.5 text-right">Paid</th>
                <th className="px-2 py-1.5 text-right">Balance</th>
                <th className="px-2 py-1.5">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {otherPager.slice.map((r) => (
                <React.Fragment key={r.pi_id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`po-other-row-${r.invoice_number?.replace(/\//g, "-")}`}
                    onClick={() => setOpenOther((o) => ({ ...o, [r.pi_id]: !o[r.pi_id] }))}>
                    <td className="px-2 py-1.5 text-slate-400">{openOther[r.pi_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px] font-bold">{r.invoice_number}</td>
                    <td className="px-2 py-1.5">{formatDate(r.invoice_date)}</td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate" title={r.vendor_name}>{r.vendor_name}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(r.po_refs || []).length ? r.po_refs.join(", ") : <span className="text-slate-400">Direct</span>}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]" data-testid={`po-other-batch-${r.invoice_number?.replace(/\//g, "-")}`}>
                      {(r.batch_numbers || []).length ? r.batch_numbers.join(", ") : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.net_payable)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-bold">{inr(r.paid)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-rose-700">{inr(r.balance)}</td>
                    <td className="px-2 py-1.5">{badge(PAY_BADGE, r.payment_status)}</td>
                  </tr>
                  {openOther[r.pi_id] && (
                    <tr className="border-t border-slate-50">
                      <td></td>
                      <td colSpan={9} className="py-2"><OtherTrail r={r} /></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filteredOthers.length === 0 && (
                <tr><td colSpan={10} className="px-2 py-4 text-center text-slate-400" data-testid="po-other-empty">No entries match.</td></tr>
              )}
            </tbody>
          </table>
          </div>

          {/* Mobile: stacked other-payment cards */}
          <div className="md:hidden divide-y divide-slate-100" data-testid="po-other-payments-cards">
            {otherPager.slice.map((r) => (
              <div key={r.pi_id} className="p-3 space-y-1.5" data-testid={`po-other-card-${r.invoice_number?.replace(/\//g, "-")}`}>
                <button className="w-full text-left" onClick={() => setOpenOther((o) => ({ ...o, [r.pi_id]: !o[r.pi_id] }))}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-slate-400 shrink-0">{openOther[r.pi_id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
                    <span className="font-mono text-[11px] font-bold">{r.invoice_number}</span>
                    {badge(PAY_BADGE, r.payment_status)}
                    <span className="ml-auto text-[10px] text-slate-400">{formatDate(r.invoice_date)}</span>
                  </div>
                  <div className="text-xs text-slate-600 break-words mt-0.5">{r.vendor_name}</div>
                </button>
                <div className="text-[10px] font-mono text-slate-500 break-words">
                  PO: {(r.po_refs || []).length ? r.po_refs.join(", ") : "Direct"}
                  {(r.batch_numbers || []).length > 0 && <> · Batch: {r.batch_numbers.join(", ")}</>}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                  <span className="text-slate-500">Net Payable: <b className="tabular-nums text-slate-800">{inr(r.net_payable)}</b></span>
                  <span className="text-emerald-700">Paid: <b className="tabular-nums">{inr(r.paid)}</b></span>
                  <span className="text-rose-700">Balance: <b className="tabular-nums">{inr(r.balance)}</b></span>
                </div>
                {openOther[r.pi_id] && <OtherTrail r={r} />}
              </div>
            ))}
            {filteredOthers.length === 0 && (
              <div className="p-4 text-center text-slate-400 text-xs">No entries match.</div>
            )}
          </div>
          <Paginator pager={otherPager} testid="po-other-payments-pager" />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-[9px] text-slate-400">{data.note}</div>
        <button data-testid="po-payments-refresh" onClick={refreshAll} disabled={refreshing} title="Refresh payment data"
          className="px-2 py-1 border border-slate-300 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /></button>
      </div>

      {canSeeRequests && (
        <PaymentRequestsPanel project={project} refreshKey={reqRefresh} onChanged={load} />
      )}

      {requestFor && (
        <RequestPaymentModal project={project} po={requestFor}
          onClose={() => setRequestFor(null)}
          onDone={() => setReqRefresh((k) => k + 1)} />
      )}
    </div>
  );
}
